import { createApi } from '@reduxjs/toolkit/query/react';
import { endpoints } from 'config/api';
import { axiosBaseQuery } from 'config/axiosBaseQuery';
import { WorkOrdersResponse, WorkOrdersQueryParams, WorkOrderStatsResponse } from './workOrdersTypes';

export const workOrdersApi = createApi({
  reducerPath: 'workOrdersApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['WorkOrder', 'Messages', 'Activity', 'Templates'],
  endpoints: (builder) => ({
    getWorkOrders: builder.query<WorkOrdersResponse, WorkOrdersQueryParams | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        if (params?.view) queryParams.append('view', params.view);
        if (params?.search) queryParams.append('search', params.search);
        if (params?.status) queryParams.append('status', params.status);
        if (params?.assignedTo) queryParams.append('assignedTo', params.assignedTo);
        if (params?.assignedToProject) queryParams.append('assignedToProject', params.assignedToProject);

        const queryString = queryParams.toString();
        return {
          url: `${endpoints.workOrders.base}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['WorkOrder'],
    }),

    getWorkOrdersStats: builder.query<WorkOrderStatsResponse, void>({
      query: () => ({
        url: endpoints.workOrders.stats,
        method: 'GET',
      }),
      providesTags: ['WorkOrder'],
    }),

    getWorkOrder: builder.query({
      query: (id) => ({
        url: endpoints.workOrders.workOrder(id),
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [{ type: 'WorkOrder', id }],
    }),

    createWorkOrder: builder.mutation({
      query: (data) => ({
        url: endpoints.workOrders.base,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['WorkOrder'],
    }),

    updateWorkOrder: builder.mutation({
      query: ({ id, data }) => ({
        url: endpoints.workOrders.workOrder(id),
        method: 'PUT',
        data,
      }),
      invalidatesTags: (_res, _err, { id }) => [{ type: 'WorkOrder', id }],
    }),

    deleteWorkOrder: builder.mutation({
      query: (id) => ({
        url: endpoints.workOrders.workOrder(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['WorkOrder'],
    }),

    assignToProject: builder.mutation({
      query: ({ id, projectId }) => ({
        url: endpoints.workOrders.assignProject(id),
        method: 'PUT',
        data: { projectId },
      }),
      invalidatesTags: [{ type: 'WorkOrder' }],
    }),

    assignToUser: builder.mutation({
      query: ({ id, userId }) => ({
        url: endpoints.workOrders.assignUser(id),
        method: 'PUT',
        data: { userId },
      }),
      invalidatesTags: [{ type: 'WorkOrder' }],
    }),

    getMessages: builder.query({
      query: (id) => ({
        url: endpoints.workOrders.messages(id),
        method: 'GET',
      }),
      transformResponse: (response: { success: boolean; data: any }) => response.data,
      providesTags: ['Messages'],
    }),

    sendMessage: builder.mutation({
      query: ({ id, message }) => ({
        url: endpoints.workOrders.messages(id),
        method: 'POST',
        data: { message },
      }),
      invalidatesTags: ['Messages'],
    }),

    getWorkOrdersTemplates: builder.query<any, void>({
      query: () => ({
        url: endpoints.workOrders.templates,
        method: 'GET',
      }),
      providesTags: ['Templates'],
    }),

    getWorkOrdersTemplate: builder.query({
      query: (id) => ({
        url: endpoints.workOrders.templateId(id),
        method: 'GET',
      }),
      providesTags: (_res, _err, id) => [{ type: 'Templates', id }],
    }),

    createWorkOrdersTemplate: builder.mutation({
      query: (template) => ({
        url: endpoints.workOrders.templates,
        method: 'POST',
        data: template,
      }),
      invalidatesTags: ['Templates'],
    }),

    deleteWorkOrdersTemplate: builder.mutation({
      query: (id) => ({
        url: endpoints.workOrders.templateId(id),
        method: 'DELETE',
      }),
      invalidatesTags: ['Templates'],
    }),
  }),
});

export const {
  useGetWorkOrdersQuery,
  useGetWorkOrdersStatsQuery,
  useGetWorkOrderQuery,
  useCreateWorkOrderMutation,
  useUpdateWorkOrderMutation,
  useDeleteWorkOrderMutation,
  useAssignToProjectMutation,
  useAssignToUserMutation,
  useGetMessagesQuery,
  useSendMessageMutation,
  useGetWorkOrdersTemplatesQuery,
  useGetWorkOrdersTemplateQuery,
  useCreateWorkOrdersTemplateMutation,
  useDeleteWorkOrdersTemplateMutation,
} = workOrdersApi;
